import {
  getElecAllMarketComparisonTable,
  getElecAllMarketsChart,
  getElecAllMarketsDaily,
} from "$/api/market-monitoring/electricity-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import LineGraph from "$/lib/components/graphs/LineGraph";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import { CheckboxSet } from "$/lib/components/molecules/ChecboxSet";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import TableGraphWidget from "$/lib/components/widgets/TableGraphWidget";
import { RenderTableNumberCell } from "$/lib/components/widgets/TableGraphWidget/RenderTableNumberCell";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { AllMarketsTableType } from "$/lib/types/api/market-monitoring/electricity-analysis.types";
import type { CellType, ColumnType } from "$/lib/types/Graphs.types";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import type { FutureProductPeriod } from "$/lib/types/models/FutureProduct.types";
import {
  format,
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import {
  BASE_ELEC_COLORS,
  handleOnChangeDefaultGraphsTabs,
  PEAK_ELEC_COLORS,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getArrayKeys, getMinMax } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

// const BASE_COLORS = ["#273c7b", "#6480c2", "#8dbdaf", "#273c7b50"];
// const PEAK_COLORS = ["#784b7a", "#ad79a2", "#f49ba7", "#784b7a80"];

type Props = {
  selectedDate: Date;
} & CustomDashboardProps;

const ElecAllMarketsChart = ({
  selectedDate,
  id,
  isDraggable = false,
  isShowcase = false,
}: Props) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [filters, setFilters] = useState<{
    base: boolean;
    peak: boolean;
  }>({
    base: true,
    peak: false,
  });

  const [tablePeriod, setTablePeriod] = useState<FutureProductPeriod | "DAY">(
    "QUARTER",
  );

  const { data, isLoading } = useQuery({
    queryKey: ["get-elec-all-markets-chart", dateRange],
    queryFn: () =>
      getElecAllMarketsChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const { data: tableData } = useQuery({
    queryKey: ["get-elec-all-markets-comparison-table", dateRange, tablePeriod],
    queryFn: () =>
      getElecAllMarketComparisonTable({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        period: tablePeriod,
      }),
  });

  const { data: daily } = useQuery({
    queryKey: ["get-elec-all-markets-prod-daily", selectedDate],
    queryFn: () => getElecAllMarketsDaily({ date: getDate(selectedDate) }),
  });

  const toDisplay = useMemo(
    () =>
      data
        ? filters.base && filters.peak
          ? data.mergedData
          : filters.base
            ? data.baseProductsData
            : filters.peak
              ? data.peakProductsData
              : []
        : [],
    [data, filters],
  );

  const baseKeys = useMemo(
    () => getArrayKeys(data?.baseProductsData ?? []),
    [data],
  );
  const peakKeys = useMemo(
    () => getArrayKeys(data?.peakProductsData ?? []),
    [data],
  );

  const keys = useMemo(
    () =>
      filters.base && filters.peak
        ? [...baseKeys, ...peakKeys]
        : filters.base
          ? baseKeys
          : filters.peak
            ? peakKeys
            : [],
    [filters, baseKeys, peakKeys],
  );

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = useMemo(
    () =>
      keys.reduce(
        (acc, key, i) => ({
          ...acc,
          [key]:
            filters.base && filters.peak
              ? [...BASE_ELEC_COLORS, ...PEAK_ELEC_COLORS][i]
              : filters.base
                ? BASE_ELEC_COLORS[i]
                : PEAK_ELEC_COLORS[i],
        }),
        {},
      ),
    [keys, filters],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  const baseTableKeys = ["cal1BaseAvg", "cal2BaseAvg", "cal3BaseAvg"];
  const peakTableKeys = ["cal1PeakAvg", "cal2PeakAvg", "cal3PeakAvg"];

  const tableBounds = useMemo(() => {
    if (tableData) {
      const keysToCompare =
        filters.base && filters.peak
          ? [...baseTableKeys, ...peakTableKeys]
          : filters.base
            ? baseTableKeys
            : peakTableKeys;

      return getMinMax(tableData[tablePeriod], keysToCompare);
    }

    return { min: { value: 0 }, max: { value: 0 } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, tablePeriod, filters]);

  const columns: ColumnType<AllMarketsTableType>[] = useMemo(() => {
    const firstHeaders: ColumnType<AllMarketsTableType>[] = [
      {
        header: () => {},
        accessorKey: "year",
        cell: (cell) => {
          const values = cell.row.original;

          const display =
            tablePeriod === "QUARTER" || tablePeriod === "MONTH"
              ? [values.quarter, values.month]
              : [values.month, values.day];

          return (
            <Flexbox className="gap-0.5 whitespace-nowrap">
              <span className="text-xs font-medium text-black">
                {cell.getValue()}
              </span>

              <span className="text-[10px] text-tertiary-600">
                {display
                  .filter((v) => v)
                  .join(tablePeriod === "DAY" ? " " : " - ")}
              </span>
            </Flexbox>
          );
        },
      },
    ];

    return [
      ...firstHeaders,
      ...(filters.base
        ? [
            {
              header: "Baseload Cal +1",
              accessorKey: "cal1BaseAvg",
              cell: (cell: CellType<AllMarketsTableType>) => (
                <RenderTableNumberCell
                  tableBounds={tableBounds}
                  reverse
                  {...cell}
                />
              ),
            },
            {
              header: "Baseload Cal +2",
              accessorKey: "cal2BaseAvg",
              cell: (cell: CellType<AllMarketsTableType>) => (
                <RenderTableNumberCell
                  tableBounds={tableBounds}
                  reverse
                  {...cell}
                />
              ),
            },
            {
              header: "Baseload Cal +3",
              accessorKey: "cal3BaseAvg",
              cell: (cell: CellType<AllMarketsTableType>) => (
                <RenderTableNumberCell
                  tableBounds={tableBounds}
                  reverse
                  {...cell}
                />
              ),
            },
          ]
        : []),

      ...(filters.peak
        ? [
            {
              header: "Peakload Cal +1",
              accessorKey: "cal1PeakAvg",
              cell: (cell: CellType<AllMarketsTableType>) => (
                <RenderTableNumberCell
                  tableBounds={tableBounds}
                  reverse
                  {...cell}
                />
              ),
            },
            {
              header: "Peakload Cal +2",
              accessorKey: "cal2PeakAvg",
              cell: (cell: CellType<AllMarketsTableType>) => (
                <RenderTableNumberCell
                  tableBounds={tableBounds}
                  reverse
                  {...cell}
                />
              ),
            },
            {
              header: "Peakload Cal +3",
              accessorKey: "cal3PeakAvg",
              cell: (cell: CellType<AllMarketsTableType>) => (
                <RenderTableNumberCell
                  tableBounds={tableBounds}
                  reverse
                  {...cell}
                />
              ),
            },
          ]
        : []),
    ];
  }, [filters, tableBounds, tablePeriod]);

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        isVisible: true,
        color:
          filters.base && filters.peak
            ? [...BASE_ELEC_COLORS, ...PEAK_ELEC_COLORS][i]
            : filters.base
              ? BASE_ELEC_COLORS[i]
              : PEAK_ELEC_COLORS[i],
      })),
    );
  }, [keys, filters]);

  return (
    <DisplayBox
      title="Ecart Calendaires France et Allemagne Base ou Peak"
      Icon={LineChartIcon}
      theme="blue"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer midHeight>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="gap-2 rounded-3xl">
            <LineGraph
              data={toDisplay}
              graphVisibility={graphVisibility}
              title="Prix Stlt EEX"
              xAxisDataKey="date"
              colors={colors}
              theme="blue"
              keys={keys}
              unit=" €/MWh"
              leftMargin={-20}
              height={530}
            />
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
          />

          <CheckboxSet<"base" | "peak">
            id="base-peak-all-markets"
            title="Base / Peak"
            selected={filters}
            onChange={(id, value) =>
              setFilters((prev) => {
                const newValue = { ...prev, [id]: value };
                if (!newValue.base && !newValue.peak) return prev;
                return newValue;
              })
            }
            options={[
              { id: "base", label: "Base" },
              { id: "peak", label: "Peak" },
            ]}
            row
            className="items-center"
          />

          <Flexbox fullHeight className="gap-4 overflow-y-auto pb-1">
            <GraphFilter
              title="Légende"
              data={graphVisibility}
              handleOnChange={handleOnChangeGraphVisiblity}
            />

            {daily && !!daily.baseData.length && !!daily.peakData.length && (
              <Flexbox fullWidth className="rounded-lg shadow-displayBoxShadow">
                <Flexbox
                  fullWidth
                  className="border-b-[1px] border-primary-300 bg-primary-200 p-1 text-sm font-medium text-black"
                >
                  <p className="w-full text-center text-sm">
                    Ecarts CAL FR-ALL actuels
                  </p>
                  <p className="w-full text-center text-sm text-primary">
                    {format(selectedDate, "dd/MM/yy")}
                  </p>
                </Flexbox>

                <Flexbox fullWidth>
                  <Flexbox
                    row
                    fullWidth
                    className="daily-prods-grid border-b-[1px] border-primary-300"
                  >
                    <Flexbox
                      fullWidth
                      fullHeight
                      align="center"
                      justify="center"
                      className="bg-primary-200 p-1 text-sm font-medium text-black"
                    >
                      <p>Base</p>
                    </Flexbox>

                    <Flexbox className="m-auto px-1 py-1">
                      {daily.baseData.map((item) => (
                        <Flexbox
                          key={item.title}
                          row
                          fullWidth
                          align="center"
                          justify="between"
                          className="mb-1 gap-3 text-xs"
                        >
                          <p>{item.title}</p>
                          <p>{item.value} €/MWh</p>
                        </Flexbox>
                      ))}
                    </Flexbox>
                  </Flexbox>

                  <Flexbox row fullWidth className="daily-prods-grid">
                    <Flexbox
                      fullWidth
                      fullHeight
                      align="center"
                      justify="center"
                      className="bg-primary-200 p-1 text-sm font-medium text-black"
                    >
                      <p>Peak</p>
                    </Flexbox>

                    <Flexbox className="m-auto px-1 py-1">
                      {daily.peakData.map((item) => (
                        <Flexbox
                          key={item.title}
                          row
                          fullWidth
                          align="center"
                          justify="between"
                          className="mb-1 gap-3 text-xs"
                        >
                          <p>{item.title}</p>
                          <p>{item.value} €/MWh</p>
                        </Flexbox>
                      ))}
                    </Flexbox>
                  </Flexbox>
                </Flexbox>
              </Flexbox>
            )}

            <Flexbox fullWidth className="p-1">
              <TableGraphWidget<AllMarketsTableType>
                columns={columns}
                data={tableData ? tableData[tablePeriod] : []}
                tableBounds={tableBounds}
                reverse
                selectedPeriod={tablePeriod}
                setSelectedPeriod={setTablePeriod}
              />
            </Flexbox>
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default ElecAllMarketsChart;
